import React, {createContext, memo, useContext} from 'react';
import {
  type ApolloError,
  type QueryHookOptions as ApolloQueryOptions,
  useQuery,
  gql,
} from '@apollo/client';
import type {
  DeliveryManagementDeliveryContextDeliveryQuery,
  DeliveryManagementDeliveryContextDeliveryQueryVariables,
  DeliveryManagementDeliveryContextDeliveryQuery_delivery as DmDelivery,
  DeliveryManagementDeliveryContextDeliveryQuery_delivery_drivers as Driver,
} from '../../__generated__/DeliveryManagementDeliveryContextDeliveryQuery';
import {deliveryTrackingLinkFragment} from './TrackingLink/utilities/showTrackingLink';

type Delivery = Omit<DmDelivery, 'drivers'> & {driver?: Driver | null};

export const DeliveryContext = createContext<{
  loading: boolean;
  delivery: Delivery | null | undefined;
  error?: ApolloError;
}>({
  loading: true,
  delivery: null,
  error: undefined,
});

export const useDelivery = () => {
  return useContext(DeliveryContext);
};

type QueryOptions = Omit<
  ApolloQueryOptions<
    DeliveryManagementDeliveryContextDeliveryQuery,
    DeliveryManagementDeliveryContextDeliveryQueryVariables
  >,
  'variables'
>;

type DeliveryProviderProps = QueryOptions & {
  children: React.ReactNode;
  variables: DeliveryManagementDeliveryContextDeliveryQueryVariables;
};

export const DeliveryProvider = ({children, variables, ...queryOptions}: DeliveryProviderProps) => {
  const queryVariables = {
    orderNumber: variables.orderNumber,
    includeDeliveryTrackingData: !!variables.includeDeliveryTrackingData,
  };
  const deliveryQueryResult = useQuery<
    DeliveryManagementDeliveryContextDeliveryQuery,
    DeliveryManagementDeliveryContextDeliveryQueryVariables
  >(DeliveryQuery, {
    errorPolicy: 'all',
    variables: queryVariables,
    ...queryOptions,
  });

  const deliveryData = deliveryQueryResult.data?.delivery;
  const delivery = deliveryData
    ? {
        __typename: deliveryData.__typename,
        assignable: deliveryData.assignable,
        driver: deliveryQueryResult.data?.delivery?.drivers?.[0] || null,
        hasTrackingInformation: deliveryData.hasTrackingInformation,
        id: deliveryData.id,
        pickupAt: deliveryData.pickupAt,
        tracking: deliveryData.tracking,
      }
    : deliveryData;

  return (
    <DeliveryContext.Provider
      value={{
        loading: deliveryQueryResult.loading,
        error: deliveryQueryResult.error,
        delivery,
      }}
    >
      {children}
    </DeliveryContext.Provider>
  );
};

export const DeliveryQuery = gql`
  query DeliveryManagementDeliveryContextDeliveryQuery(
    $orderNumber: String!
    $includeDeliveryTrackingData: Boolean!
  ) {
    delivery: deliveryByOrderNumber(orderNumber: $orderNumber) {
      id
      assignable
      drivers {
        id
        fullName
      }
      ...DeliveryTrackingLinkFragment @include(if: $includeDeliveryTrackingData)
    }
  }
  ${deliveryTrackingLinkFragment.delivery}
`;

export default memo(DeliveryProvider);
